/**
 * =========================================================================================
 * Shared Cashfree Payment Hook
 * =========================================================================================
 * Handles opening Cashfree checkout/subscription flows.
 * Used by resume-payment-banner, payments page, and any future component
 * that needs to trigger a Cashfree payment.
 */

import { useState } from 'react';
import useUAlert from 'ubs-framework/v2/UAlert';
import { getCashfreeInstance } from './cashfree-loader';

const useCashfreePayment = () => {
    const alert = useUAlert();
    const [isLoading, setIsLoading] = useState(false);

    /**
     * Trigger Cashfree payment based on action type
     * @param {string} action - RESUME_AUTH | RESUME_PAYMENT
     * @param {object} data - Response data from resumePayment API
     * @returns {Promise<boolean>} - true if payment was initiated
     */
    const triggerPayment = async (action, data) => {
        try {
            setIsLoading(true);
            const cashfreeInstance = await getCashfreeInstance();

            if (action === 'RESUME_AUTH' || action === 'RESUME_PAYMENT_METHOD_UPDATE') {
                const sessionId = data?.subscription_session_id;
                if (!sessionId) {
                    setIsLoading(false);
                    return false;
                }
                await cashfreeInstance.subscriptionsCheckout({
                    subsSessionId: sessionId,
                    redirectTarget: '_self'
                });
                return true;
            }

            if (action === 'RESUME_PAYMENT') {
                const paymentLink = data?.proratedOrder?.paymentLink;
                if (!paymentLink) {
                    setIsLoading(false);
                    return false;
                }
                await cashfreeInstance.checkout({
                    paymentSessionId: paymentLink,
                    redirectTarget: '_self',
                });
                return true;
            }

            setIsLoading(false);
            return false;
        } catch (err) {
            alert.show({ title: 'Payment Error', description: 'Failed to initiate payment. Please try again.' });
            setIsLoading(false);
            return false;
        }
    };

    return { triggerPayment, isLoading };
};

export default useCashfreePayment;
